// Pure aggregation helpers for the daily-view dashboard.
//
// All inputs are the raw master_production_plan rows that the PJ Daily
// Solution ships in `run.result_tables.master_production_plan`. The dashboard
// re-aggregates client-side so changing the day filter doesn't require a
// roundtrip — the rows themselves never change.

import type { ChipStats, MPPRow, SelectedTarget } from './types';

// Pipeline reads left-to-right: raw materials → bulk formulation → container
// → packaged SKU → customer order. SKU sits between container and order
// because a SKU = bulk-formulation-in-a-specific-container; the order is the
// customer-facing demand for that SKU.
export const STAGES = ['raws', 'bulk', 'container', 'sku', 'order'] as const;
export type Stage = (typeof STAGES)[number];

export const STAGE_COLORS: Record<string, string> = {
  raws: '#d97706',
  bulk: '#a16207',
  container: '#0891b2',
  sku: '#16a34a',
  order: '#dc2626',
  // overlay stages that may appear in older payloads
  filled_from: '#2563eb',
  po_vendor: '#f59e0b',
  'rm type': '#7c3aed',
};

/**
 * Human-friendly stage labels for the toggle UI. (`raws` reads better as
 * "Raw materials" in a button than the bare column name.)
 */
export const STAGE_LABELS: Record<Stage, string> = {
  raws: 'Raw materials',
  bulk: 'Bulk',
  container: 'Container',
  sku: 'SKU',
  order: 'Order',
};

export const RM_COLORS: Record<string, string> = {
  'solon fg': '#2563eb',
  manufactured: '#a16207',
  tolled: '#7c3aed',
  unfilled: '#dc2626',
};

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

export function filterRowsByModelDay(
  rows: readonly MPPRow[],
  model: string,
  day: string,
): MPPRow[] {
  return rows.filter(
    (r) =>
      r.model_type === model && (day === 'all' || String(r.day) === day),
  );
}

export function filledRows(rows: readonly MPPRow[]): MPPRow[] {
  return rows.filter((r) => r.filled_from !== 'Dropped / Unfilled');
}

export function unfilledRowsOnly(rows: readonly MPPRow[]): MPPRow[] {
  return rows.filter((r) => r.filled_from === 'Dropped / Unfilled');
}

// ---------------------------------------------------------------------------
// Chip stats
// ---------------------------------------------------------------------------

export function chipStats(
  rows: readonly MPPRow[],
  model: string,
  day: string,
): ChipStats {
  const allForModel = rows.filter((r) => r.model_type === model);
  const filledAll = filledRows(allForModel);
  const unfilledAll = unfilledRowsOnly(allForModel);
  const slice = filledRows(filterRowsByModelDay(rows, model, day));
  const lbsShipped = slice.reduce((a, r) => a + (Number(r.s_to_o) || 0), 0);

  // Requested lbs: take max order_lbs per (order, sku) pair across the model.
  const byOrderSku = new Map<string, number>();
  filledAll.forEach((r) => {
    const k = `${r.order ?? ''}|${r.sku ?? ''}`;
    const v = Number(r.order_lbs) || 0;
    byOrderSku.set(k, Math.max(byOrderSku.get(k) ?? 0, v));
  });
  const lbsRequested = Array.from(byOrderSku.values()).reduce(
    (a, v) => a + v,
    0,
  );

  const critRows = filledAll.filter((r) => r.critical_po === 'Yes');
  return {
    filledOrders: new Set(slice.map((r) => r.order)).size,
    unfilledOrders: new Set(unfilledAll.map((r) => r.order)).size,
    lbsShipped,
    lbsRequested,
    criticalOrders: new Set(critRows.map((r) => r.order)).size,
    criticalFlowRows: critRows.length,
    fillRate:
      filledAll.length / (filledAll.length + unfilledAll.length) || 0,
  };
}

// ---------------------------------------------------------------------------
// Sankey reconstruction from MPP rows.
// Re-builds {nodes, links} for the active model+day slice so the chart can
// reflect the day filter without a backend round-trip.
// ---------------------------------------------------------------------------

export interface SankeyNodeOut {
  id: string;
  stage: Stage | string;
  label: string;
}
export interface SankeyLinkOut {
  source: string;
  target: string;
  value: number;
  lbs: number;
  units: number;
  rows: number;
}

export function buildSankeyFromMPP(
  rows: readonly MPPRow[],
  // Subset of STAGES to render. Hidden stages contribute no nodes; remaining
  // stages reconnect through the gaps. Defaults to all stages visible.
  visibleStages: readonly Stage[] = STAGES,
): { nodes: SankeyNodeOut[]; links: SankeyLinkOut[] } {
  const nodeMap = new Map<string, SankeyNodeOut>();
  const linkMap = new Map<string, SankeyLinkOut>();
  // Adjacency is checked against `visibleStages` so toggling a column off
  // (e.g. hiding `bulk`) lets the remaining stages link directly. Rows whose
  // data has a genuine gap (e.g. raws + order with no bulk) still get their
  // would-be cross-column stroke dropped — we keep the no-skipping rule
  // relative to the *visible* lineup.
  const visIdx = new Map<Stage, number>(visibleStages.map((s, i) => [s, i]));
  for (const r of rows) {
    if (r.filled_from === 'Dropped / Unfilled') continue;
    const chain: { stage: Stage; label: string }[] = [];
    for (const s of visibleStages) {
      const v = r[s];
      if (v != null && String(v).trim() !== '') {
        chain.push({ stage: s, label: String(v).trim() });
      }
    }
    if (chain.length < 2) continue;
    for (const c of chain) {
      const id = `${c.stage}::${c.label}`;
      if (!nodeMap.has(id))
        nodeMap.set(id, { id, stage: c.stage, label: c.label });
    }
    for (let i = 0; i < chain.length - 1; i++) {
      const a = chain[i];
      const b = chain[i + 1];
      const ai = visIdx.get(a.stage) ?? -1;
      const bi = visIdx.get(b.stage) ?? -1;
      if (ai < 0 || bi !== ai + 1) continue; // adjacent in visible lineup only
      const src = `${a.stage}::${a.label}`;
      const tgt = `${b.stage}::${b.label}`;
      const k = `${src}=>${tgt}`;
      let e = linkMap.get(k);
      if (!e) {
        e = { source: src, target: tgt, value: 0, lbs: 0, units: 0, rows: 0 };
        linkMap.set(k, e);
      }
      const lbs = Number(r.s_to_o) || 0;
      e.value += lbs;
      e.lbs += lbs;
      e.units += rowUnits(r);
      e.rows += 1;
    }
  }
  // Drop isolated nodes (no incident surviving link). A row whose only present
  // stages are non-adjacent in the visible lineup (e.g. raws + order with
  // bulk/container/sku empty) adds nodes but no link — d3-sankey then chokes on
  // the orphan ("Cannot read properties of undefined (reading 'length')") and
  // white-screens the chart. Full datasets mask it; narrowing (Top-N, coverage)
  // exposes it. Keeping only linked nodes guarantees a layout-safe graph.
  const links = [...linkMap.values()].filter((l) => l.value > 0);
  const used = new Set<string>();
  for (const l of links) {
    used.add(l.source);
    used.add(l.target);
  }
  return {
    nodes: [...nodeMap.values()].filter((n) => used.has(n.id)),
    links,
  };
}

// ---------------------------------------------------------------------------
// Critical-PO + Unfilled tables
// ---------------------------------------------------------------------------

export interface CriticalRow {
  order: string;
  sku: string;
  day: number | string;
  vendor: string;
  lands: string;
  conf: string;
}

export function criticalPORows(
  rows: readonly MPPRow[],
  model: string,
  day: string,
): CriticalRow[] {
  const slice = rows.filter(
    (r) =>
      r.model_type === model &&
      r.critical_po === 'Yes' &&
      (day === 'all' || String(r.day) === day),
  );
  const out: CriticalRow[] = [];
  const seen = new Set<string>();
  for (const r of slice) {
    const k = `${r.order}|${r.sku}|${r.day}`;
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({
      order: r.order ?? '',
      sku: stripCode(r.sku ?? ''),
      day: r.day ?? '',
      vendor: r.po_vendor ?? '—',
      lands: r.po_landing_date ?? '—',
      conf: r.po_confirmed ?? '—',
    });
  }
  return out;
}

export interface UnfilledRow {
  order: string;
  sku: string;
  lbs: number;
}

export function unfilledOrderRows(
  rows: readonly MPPRow[],
  model: string,
): UnfilledRow[] {
  const u = rows.filter(
    (r) => r.model_type === model && r.filled_from === 'Dropped / Unfilled',
  );
  const agg = new Map<string, { order: string; sku: string; lbs: number }>();
  for (const r of u) {
    const k = `${r.order ?? ''}|${r.sku ?? ''}`;
    const cur = agg.get(k) ?? {
      order: r.order ?? '',
      sku: r.sku ?? '',
      lbs: 0,
    };
    cur.lbs += Number(r.s_to_o) || 0;
    agg.set(k, cur);
  }
  return Array.from(agg.values())
    .map((x) => ({ ...x, sku: stripCode(x.sku) }))
    .sort((a, b) => b.lbs - a.lbs);
}

// ---------------------------------------------------------------------------
// Supply-mix donut
// ---------------------------------------------------------------------------

export interface DonutSlice {
  key: string;
  value: number;
}

export function rmTypeMix(rows: readonly MPPRow[]): DonutSlice[] {
  const m = new Map<string, number>();
  for (const r of rows) {
    const k = r['rm type'] ?? 'unknown';
    m.set(k, (m.get(k) ?? 0) + (Number(r.s_to_o) || 0));
  }
  return Array.from(m, ([key, value]) => ({ key, value })).filter(
    (d) => d.value > 0,
  );
}

// ---------------------------------------------------------------------------
// Detail-panel content for a selected node or link
// ---------------------------------------------------------------------------

export interface OrderAgg {
  au: string;
  customer: string;
  sku: string;
  lbs: number;
  day: number | string;
}
export interface POAgg {
  vendor: string;
  poNumber: string;
  lands: string;
  conf: string;
  qty: number;
}
export interface SupplyAgg {
  stage: string;
  label: string;
  lbs: number;
}
export interface DayAgg {
  day: number | string;
  lbs: number;
  flows: number;
}

export interface DetailContent {
  stageLabel: string;
  primaryLabel: string;
  stageColor: string;
  lbsIn: number;
  lbsOut: number;
  ordersTouched: number;
  flowRows: number;
  orders: OrderAgg[];
  pos: POAgg[];
  supply: SupplyAgg[];
  byDay: DayAgg[];
}

interface SankeyLaidLinkLike {
  source: { id: string; stage: string; label: string };
  target: { id: string; stage: string; label: string };
  lbs: number;
}

interface SankeyLaidNodeLike {
  id: string;
  stage: string;
  label: string;
}

// hiNodes: set of node ids in the highlighted flow chain (used to summarise
// the supply chain for a node selection).
export function buildDetailContent(
  sel: SelectedTarget,
  rowsForDay: readonly MPPRow[],
  laidLinks: readonly SankeyLaidLinkLike[],
  laidNodes: readonly SankeyLaidNodeLike[],
  hiNodes: ReadonlySet<string>,
): DetailContent | null {
  const filled = filledRows(rowsForDay);
  let stageLabel: string;
  let primaryLabel: string;
  let stageColor: string;
  let rowsTouching: MPPRow[];

  if (sel.kind === 'node') {
    const node = laidNodes.find((n) => n.id === sel.nodeId);
    if (!node) return null;
    stageLabel = node.stage;
    primaryLabel = node.label;
    stageColor = STAGE_COLORS[node.stage] ?? '#64748b';
    rowsTouching = filled.filter(
      (r) => (r as unknown as Record<string, unknown>)[node.stage] === node.label,
    );
  } else {
    const lk = laidLinks.find(
      (l) => `${l.source.id}=>${l.target.id}` === sel.linkKey,
    );
    if (!lk) return null;
    stageLabel = `${lk.source.stage} → ${lk.target.stage}`;
    primaryLabel = `${lk.source.label} → ${lk.target.label}`;
    stageColor = STAGE_COLORS[lk.source.stage] ?? '#64748b';
    rowsTouching = filled.filter((r) => {
      const rr = r as unknown as Record<string, unknown>;
      return (
        rr[lk.source.stage] === lk.source.label &&
        rr[lk.target.stage] === lk.target.label
      );
    });
  }

  // Lbs in/out from sankey link totals.
  let lbsIn = 0;
  let lbsOut = 0;
  if (sel.kind === 'node') {
    for (const l of laidLinks) {
      if (l.target.id === sel.nodeId) lbsIn += l.lbs;
      if (l.source.id === sel.nodeId) lbsOut += l.lbs;
    }
  } else {
    const lk = laidLinks.find(
      (l) => `${l.source.id}=>${l.target.id}` === sel.linkKey,
    );
    lbsIn = lbsOut = lk?.lbs ?? 0;
  }

  const ordersSet = new Set<string>();
  rowsTouching.forEach((r) => {
    if (r.order) ordersSet.add(r.order);
  });

  // Customer orders downstream
  const orderMap = new Map<
    string,
    { lbs: number; sku: string; day: number | string }
  >();
  for (const r of rowsTouching) {
    const k = r.order ?? '';
    const cur = orderMap.get(k) ?? { lbs: 0, sku: r.sku ?? '', day: r.day ?? '' };
    cur.lbs += Number(r.s_to_o) || 0;
    orderMap.set(k, cur);
  }
  const orders: OrderAgg[] = Array.from(orderMap.entries())
    .map(([order, agg]) => {
      const m = order.match(/^(.*)\s*\((AU\d+)\)\s*$/);
      return {
        au: m ? m[2] : '',
        customer: m ? m[1].trim() : order,
        sku: stripCode(agg.sku),
        lbs: agg.lbs,
        day: agg.day,
      };
    })
    .sort((a, b) => b.lbs - a.lbs);

  // Vendor PO chain
  const poMap = new Map<
    string,
    { vendor: string; poNumber: string; lands: string; conf: string; qty: number }
  >();
  for (const r of rowsTouching) {
    if (!r.po_number && !r.po_vendor) continue;
    const vendor = r.po_vendor ?? '—';
    const poNumber = r.po_number ?? '—';
    const k = `${vendor}|${poNumber}`;
    const cur = poMap.get(k) ?? {
      vendor,
      poNumber,
      lands: r.po_landing_date ?? '—',
      conf: r.po_confirmed ?? '—',
      qty: 0,
    };
    cur.qty += poQtyNum(r.po_qty);
    poMap.set(k, cur);
  }
  const pos = Array.from(poMap.values());

  // Supply chain — other nodes in the highlighted chain
  const supply: SupplyAgg[] = [];
  if (sel.kind === 'node') {
    const stageOrder: Record<string, number> = {
      raws: 0,
      bulk: 1,
      container: 2,
      order: 3,
    };
    for (const nid of hiNodes) {
      if (nid === sel.nodeId) continue;
      const n = laidNodes.find((x) => x.id === nid);
      if (!n) continue;
      const lbs = laidLinks
        .filter((l) => l.source.id === nid || l.target.id === nid)
        .reduce((a, l) => a + l.lbs, 0);
      supply.push({ stage: n.stage, label: n.label, lbs });
    }
    supply.sort(
      (a, b) =>
        (stageOrder[a.stage] ?? 9) - (stageOrder[b.stage] ?? 9) ||
        b.lbs - a.lbs,
    );
  }

  // By day
  const dayMap = new Map<string, { lbs: number; flows: number }>();
  for (const r of rowsTouching) {
    const k = String(r.day ?? '—');
    const cur = dayMap.get(k) ?? { lbs: 0, flows: 0 };
    cur.lbs += Number(r.s_to_o) || 0;
    cur.flows += 1;
    dayMap.set(k, cur);
  }
  const byDay: DayAgg[] = Array.from(dayMap.entries())
    .map(([day, agg]) => ({ day, lbs: agg.lbs, flows: agg.flows }))
    .sort((a, b) => Number(a.day) - Number(b.day));

  return {
    stageLabel,
    primaryLabel,
    stageColor,
    lbsIn,
    lbsOut,
    ordersTouched: ordersSet.size,
    flowRows: rowsTouching.length,
    orders,
    pos,
    supply,
    byDay,
  };
}

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

// Strip the trailing "(F1234.56)" code from a SKU string for compact display.
export function stripCode(s: string): string {
  return s.replace(/\s*\([^)]+\)\s*$/, '');
}

// Container units allocated to an order. "PJ Daily Solution 2026-06-09" emits
// this measure as `c_to_o`; older daily models named the same thing
// `units_to_fill`. Coalesce so the dashboard reads either model's payload.
export function rowUnits(r: MPPRow): number {
  return Number(r.c_to_o ?? r.units_to_fill) || 0;
}

// PO quantity as a number. Older models emit a numeric `po_qty`; the
// 2026-06-09 model emits a comma-joined string ("100, 200") or "-".
export function poQtyNum(v: MPPRow['po_qty']): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
  if (typeof v !== 'string') return 0;
  return v
    .split(',')
    .map((p) => Number(p.trim()))
    .filter((n) => Number.isFinite(n))
    .reduce((a, n) => a + n, 0);
}
